import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/api';

/**
 * #2025 — profile-verification gate screen. A rep is routed here on login when
 * an admin flagged them OR their home address is incomplete (see the
 * ProtectedRoute gate + the login `profile_verification` signal). Saving
 * geocodes the address (lat/lon now; home timezone once #2027 enables the
 * Time Zone API) and clears the gate, so the rep's home tz stays trustworthy
 * for Start/End-Day localization (#1426).
 */

interface StoredAssociate {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

function readAssociate(): StoredAssociate {
  try {
    return (JSON.parse(localStorage.getItem('associate') || '{}') as StoredAssociate) || {};
  } catch {
    return {};
  }
}

function readReason(): string | null {
  try {
    return (
      (JSON.parse(localStorage.getItem('profileVerification') || 'null') as { reason?: string } | null)?.reason ?? null
    );
  } catch {
    return null;
  }
}

export default function ProfileVerify() {
  const navigate = useNavigate();
  const initial = readAssociate();
  const reason = readReason();

  const [address1, setAddress1] = useState(initial.address1 || '');
  const [address2, setAddress2] = useState(initial.address2 || '');
  const [city, setCity] = useState(initial.city || '');
  const [stateVal, setStateVal] = useState(initial.state || '');
  const [zip, setZip] = useState(initial.zip || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = [address1, city, stateVal, zip].every((v) => v.trim().length > 0) && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch('/api/auth/profile/verify/', {
        method: 'POST',
        body: JSON.stringify({
          address1: address1.trim(),
          address2: address2.trim() || undefined,
          city: city.trim(),
          state: stateVal.trim(),
          zip: zip.trim(),
        }),
      });
      // Gate cleared server-side; clear the local signal and continue into the app.
      localStorage.removeItem('profileVerification');
      const merged = { ...readAssociate(), address1, address2, city, state: stateVal, zip };
      localStorage.setItem('associate', JSON.stringify(merged));
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="mb-5">
          <h1 className="font-heading text-xl font-semibold text-slate-800">Confirm your profile</h1>
          <p className="mt-1 text-sm text-slate-500">
            {reason === 'flagged'
              ? 'Your manager asked you to confirm your details before continuing.'
              : 'We need your home address to keep your time and mileage accurate.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-xl bg-white p-4 shadow-card">
          <label className="mb-1 block text-sm font-medium text-slate-700">Home address</label>
          <input
            value={address1}
            onChange={(e) => setAddress1(e.target.value)}
            placeholder="Street address"
            className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          />
          <input
            value={address2}
            onChange={(e) => setAddress2(e.target.value)}
            placeholder="Apt / unit (optional)"
            className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          />
          <div className="mb-2 flex gap-2">
            <input
              value={stateVal}
              onChange={(e) => setStateVal(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="State"
              className="w-24 rounded-lg border border-slate-300 px-3 py-2.5 uppercase text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
            <input
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              inputMode="numeric"
              placeholder="ZIP"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>

          {error && <p className="mb-2 text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-2 w-full rounded-lg bg-primary-600 py-3 font-semibold text-white transition-colors hover:bg-primary-700 disabled:bg-slate-300"
          >
            {submitting ? 'Saving…' : 'Save & continue'}
          </button>
          <p className="mt-2 text-center text-[11px] text-slate-400">
            We use this only to calculate your drive time and timezone.
          </p>
        </form>
      </div>
    </div>
  );
}
